// @ts-nocheck
/**
 * NPC詳細モーダル
 */

import React from 'react';
import type { NpcId, AdventureProgress } from '../data/types';
import { useNpcDetail } from '../hooks/useAdventure';
import { getFirstClearRewardPreview, formatRewardText } from '../systems/rewardCalculator';
import { modalStyles as styles, getTierColor } from '../styles/adventureStyles';

interface NpcDetailModalProps {
  npcId: NpcId | null;
  progress: AdventureProgress;
  isOpen: boolean;
  onClose: () => void;
  onStartBattle: (npcId: NpcId) => void;
}

export const NpcDetailModal: React.FC<NpcDetailModalProps> = ({
  npcId,
  progress,
  isOpen,
  onClose,
  onStartBattle,
}) => {
  const detail = useNpcDetail(npcId, progress);

  if (!isOpen || !detail) return null;

  const {
    name,
    portrait,
    style,
    quotes,
    deckName,
    tier,
    difficultyText,
    status,
    record,
    isFirstBattle,
    remainingRewards,
    repeatReward,
  } = detail;

  const rewardPreview = getFirstClearRewardPreview(npcId!);

  // パックタイプの表示名
  const getPackTypeName = (type: string) => {
    const names: Record<string, string> = {
      normal: 'ノーマルパック',
      fire: '炎パック',
      water: '水パック',
      light: '光パック',
      dark: '闇パック',
      primitive: '原始パック',
      future: '未来パック',
      royal: 'ロイヤルパック',
    };
    return names[type] || type;
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.content} onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div style={styles.header}>
          <div style={styles.portrait}>{portrait}</div>
          <div>
            <h2 style={styles.name}>{name}</h2>
            <div style={{ color: getTierColor(tier), fontSize: '14px' }}>
              難易度: {difficultyText} ({tier})
            </div>
          </div>
        </div>

        {/* ボディ */}
        <div style={styles.body}>
          {/* 開始時の台詞 */}
          <div style={styles.quote}>
            「{quotes?.start || '...'}」
          </div>

          {/* 情報グリッド */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>対戦情報</div>
            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>スタイル</div>
                <div style={styles.infoValue}>{style}</div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>使用デッキ</div>
                <div style={styles.infoValue}>{deckName}</div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>戦績</div>
                <div style={styles.infoValue}>
                  {record ? `${record.wins}勝 ${record.losses}敗` : '未挑戦'}
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>本日の残り報酬</div>
                <div style={styles.infoValue}>
                  {isFirstBattle ? '初回クリア' : `${remainingRewards}/3回`}
                </div>
              </div>
            </div>
          </div>

          {/* 報酬プレビュー */}
          {isFirstBattle && rewardPreview.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>初回クリア報酬</div>
              <ul style={styles.rewardList}>
                {rewardPreview.map((line, i) => (
                  <li key={i} style={styles.rewardItem}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          {/* 再戦報酬の説明 */}
          {!isFirstBattle && repeatReward && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>再戦報酬</div>
              <ul style={styles.rewardList}>
                <li style={styles.rewardItem}>
                  💰 {repeatReward.goldMin.toLocaleString()}〜{repeatReward.goldMax.toLocaleString()}G
                </li>
                {repeatReward.packType && repeatReward.packChance > 0 && (
                  <li style={styles.rewardItem}>
                    🎴 {getPackTypeName(repeatReward.packType)} (確率: {Math.round(repeatReward.packChance * 100)}%)
                  </li>
                )}
              </ul>
              <div style={{ fontSize: '13px', color: '#a0a0a0', marginTop: '8px' }}>
                1日3回まで報酬を獲得できます。
                {remainingRewards === 0 ? (
                  <span style={{ color: '#ff6b6b' }}>
                    {' '}(本日の報酬は獲得済み)
                  </span>
                ) : (
                  <span style={{ color: '#6bff6b' }}>
                    {' '}(残り{remainingRewards}回)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* フッター */}
        <div style={styles.footer}>
          <button
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onClick={onClose}
          >
            閉じる
          </button>
          <button
            style={{ ...styles.button, ...styles.buttonPrimary }}
            onClick={() => onStartBattle(npcId!)}
          >
            ⚔️ 対戦する
          </button>
        </div>
      </div>
    </div>
  );
};

export default NpcDetailModal;
